import Navbar from "./Navbar/NavBar";
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    ConfigProvider,
    Layout,
    List,
    Progress,
    Row,
    Select,
    Tag,
    Typography
} from "antd";
import React, {useState} from "react";
import {DIHEDRAL_ANGLE_GROUP_DESCRIPTIONS} from "./GeometryUtil";
import {InfoCircleTwoTone, PlusCircleOutlined, SearchOutlined, SelectOutlined} from "@ant-design/icons";
import {dependentConformationMap, marginalOptionValues} from "./Configuration";
import {faGreaterThanEqual, faLessThan, faLessThanEqual} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    ALLOWED_COLOR_LIGHT,
    FAVORED_COLOR_LIGHT,
    OUTLIER_COLOR_DARK,
    OUTLIER_COLOR_LIGHT
} from "./ColorUtil";

function Tutorial() {
    const mapFunc = (currenIndex) => {
        const illegalStates = new Set(currenIndex.flatMap(val => dependentConformationMap[val]))
        return marginalOptionValues.map(optionVal => ({
            label: optionVal,
            value: optionVal,
            disabled: illegalStates.has(optionVal)
        }))
    }

    const [marginalConfiguration, setMarginalConfiguration] = useState(mapFunc([]))

    return (
        <div>
            <div style={{position: 'sticky', top: 0, zIndex: 10000}}>
                <Navbar/>
            </div>
            <div>

                <Layout style={{backgroundColor: '#FFFFFF'}}>
                    <Layout.Content
                        style={{display: "flex", flexDirection: "column", backgroundColor: '#FFFFFF', gap: 40, lineHeight:"2em", paddingLeft:'2vw', paddingTop:'2vh'}}>
                        <ConfigProvider
                            theme={{components: {Progress: {circleTextColor: "#000000", circleTextFontSize: "1.5em"}}}}>
                            <Row style={{justifyContent: "left"}}>
                                <Col span={24}>
                                    <Typography.Title level={3}> Tutorial for PhiSiCal (φψχal)
                                        Checkup </Typography.Title>
                                </Col>
                            </Row>
                            <Row style={{justifyContent: "left"}}>
                                <Col span={1}/>
                                <Col span={23}>
                                    <Card bordered={true} style={{width: "fit-content", paddingRight: '5vw'}}>
                                        <ul>
                                            <li><a href={"#introduction"} style={{display:"block"}}><h3>Introduction</h3></a></li>
                                            <li><a href={"#getting_started"} style={{display:"block"}}><h3>Getting Started</h3></a></li>
                                            <ul>
                                                <li><a href={"#configuring_conformation_panel"} style={{display:"block"}}><h3>Configuring Dihedral
                                                    Angle conformations for evaluation</h3></a></li>
                                                <ul>
                                                    <li><a href={"#configuring_marginal_conformations"} style={{display:"block"}}><h3>Configuring
                                                        marginal conformations for evaluation</h3></a></li>
                                                    <li><a href={"#configuring_marginal_conformations"} style={{display:"block"}}><h3>Configuring
                                                        conditional conformations for evaluation</h3></a></li>
                                                    <li><a href={"#configuring_joint_conformations"} style={{display:"block"}}><h3>Configuring
                                                        joint conformation for evaluation</h3></a></li>
                                                </ul>
                                            </ul>
                                            <li><a href={"#phisical_checkup_output"} style={{display:"block"}}><h3>PhiSiCal-Checkup output</h3>
                                            </a></li>
                                            <ul>
                                                <li><a href={"#phisical_checkup_output_table"} style={{display:"block"}}><h3>PhiSiCal-Checkup
                                                    Report</h3></a></li>
                                                <li><a href={"#phisical_checkup_output_on_structure"} style={{display:"block"}}><h3>Visualize on
                                                    structure</h3></a></li>
                                                <li><a href={"#phisical_checkup_output_contour_analysis"} style={{display:"block"}}><h3>Contour
                                                    analysis</h3></a></li>
                                            </ul>
                                            <li><a href={"#phisical_checkup_coloring"} style={{display:"block"}}><h3>Assigning conformations to
                                                validation regions</h3></a></li>
                                        </ul>
                                    </Card>
                                </Col>
                            </Row>

                            <Row id={"introduction"} style={{justifyContent: "left", scrollMarginTop:'10vh', scrollSnapMarginTop:'10vh'}}>
                                <Col span={22}>
                                    <Typography.Title level={3}> Introduction </Typography.Title>
                                    PhiSiCal (φψχal) Checkup is a comprehensive Bayesian and information-theoretic
                                    framework
                                    for validation of amino acid dihedral angle conformations in proteins. Internally PhiSiCal-Checkup
                                    uses <i>PhiSiCal-Mixture models</i> inferred using information-theoretic criterion
                                    of minimum message length (MML).
                                    The front-end of the PhiSiCal-Checkup web application is built with the React
                                    framework,
                                    while the computations are geared by a C++ backend application. The offline version of PhiSiCal-Checkup can be downloaded from
                                    <a href="https://lcb.infotech.monash.edu/phisical/checkup/download" target={"_blank"} rel={"noreferrer noopener"}> here</a>.
                                </Col>
                            </Row>

                            <Row style={{justifyContent: "left", scrollMarginTop:'10vh', scrollSnapMarginTop:'10vh'}} id={"getting_started"}>
                                <Col span={22}>
                                    <Typography.Title level={3}> Getting started </Typography.Title>
                                    PhiSiCal-Checkup supports validation of amino acid dihedral angle conformation of  protein in structure models.
                                    The validation process can be initiated
                                    for a protein structure model either by providing the PDB ID <Progress type="circle"
                                                                                                           success={{
                                                                                                               percent: 99.9,
                                                                                                               strokeColor: "#1eed19"
                                                                                                           }}
                                                                                                           format={() => '1'}
                                                                                                           size={28}/> or
                                    uploading the atomic coordinate
                                    file <Progress type="circle" success={{percent: 99.9, strokeColor: "#1eed19"}}
                                                   format={() => '2'} size={28}/>.
                                    <div style={{display: "flex", justifyContent: "center"}}>
                                        <img src={process.env.PUBLIC_URL + "/tutorial/form.png"}
                                             alt="PhiSiCal-Checkup submit form"/>
                                    </div>
                                    <span style={{whiteSpace: "break-spaces" , fontWeight:"bold", fontSize:"1.1em"}}>
                                        {"Further, the PhiSiCal-Checkup submission form features:\n"}</span>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                        <ul>
                                            <li>Multiple options for the set of mixture models to be used in validation.{"\n"}</li>
                                            <span style={{whiteSpace: "break-spaces"}}>
                                        Use the dropdown menu <Progress type="circle" success={{
                                                percent: 99.9,
                                                strokeColor: "#1eed19"
                                            }}
                                                                        format={() => '3'} size={28}/> to select the mixture model to be used for evaluation.{"\n"}
                                        By default the system uses the mixture models inferred from the Top2018 dataset (<a
                                                href="https://onlinelibrary.wiley.com/doi/full/10.1002/pro.4239"
                                                target={"_blank"}
                                                rel={"noreferrer noopener"}>{"Williams et al. (2022)"}</a>). This can be changed by selecting a different option from the dropdown.
                                                {"\n"}
                                        </span>
                                        </ul>
                                    </span>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                        <ul>
                                            <li>Customizable set of dihedral angle conformations for evaluation.{"\n"}</li>
                                            <span style={{whiteSpace: "break-spaces"}}>
                                                Use the button <Progress type="circle" success={{percent: 99.9, strokeColor: "#1eed19"}} format={() => '4'} size={28}/> to display
                                                the configuration panel.{"\n"}
                                                If not explicitly provided, by default PhiSiCal-Checkup evaluates the following dihedral conformations of each residue:
                                                <ul>
                                                    <li><i> All individual dihedral angles</i></li>
                                                    <li>Ramachandran ({"<φ,ψ>"}) dihedral angles</li>
                                                    <li>Main chain ({"<ω,φ,ψ>"}) dihedral angles</li>
                                                    <li>Side chain dihedral angles</li>
                                                    <li>{"<ω, φ, ψ, χ₁, ..., Xₙ>"}&nbsp;all combined (Joint)</li>
                                                    <li> Side chain dihedral angles given Ramachandran ({"<φ,ψ>"}) dihedral angles</li>
                                                </ul>
                                                More details on specifying a custom set of dihedral angle conformations for evaluation is explained in the section <a href="#configuring_conformation_panel">Configuring Dihedral Angle conformations for evaluation</a>{".\n"}
                                        </span>
                                        </ul>
                                    </span>

                                    <span style={{whiteSpace: "break-spaces"}}>On completion of the filling the PhiSiCal-Checkup submission form, {"\n"}
                                        click <Button type="primary" htmlType="submit">Submit</Button> <Progress type="circle" success={{percent: 99.9, strokeColor: "#1eed19"}}
                                                format={() => '5'} size={28}/> to generate PhiSiCal-Checkup report.
                                    Refer section <a href={"#phisical_checkup_output"}>PhiSiCal-Checkup output</a> for more details.{"\n"}
                                </span>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                 Or use, <Button type="default">Reset</Button> <Progress type="circle" success={{
                                        percent: 99.9,
                                        strokeColor: "#1eed19"
                                    }} format={() => '6'} size={28}/> button to reset all the fields.
                            </span>

                                </Col>
                            </Row>

                            <Row id="configuring_conformation_panel" style={{scrollMarginTop:'10vh', scrollSnapMarginTop:'10vh'}}>
                                <Col span={22}>
                                    <Typography.Title level={4}> Configuring Dihedral Angle conformations for
                                        evaluation </Typography.Title>
                                    In PhiSiCal-Checkup users have the flexibility to specify and evaluate any
                                    conformation resultant from any possible combination of
                                    dihedral angle terms (formed from one or more dihedral angle terms).
                                    For convenience, we categorize these conformations into three main
                                    types:

                                    <ul style={{marginTop: "1vh"}}>
                                        <li><b> Marginal conformation</b> - any combination of <i>d</i> dihedral angle
                                            terms. <i>d</i> quantifies the number of dihedral angle terms
                                        </li>
                                        <li><b> Conditional conformation</b> - any subset of dihedral angles subjected
                                            to observing specific values for others
                                        </li>
                                        <li><b> Joint conformation</b> - {"<ω, φ, ψ, χ₁, ..., Xₙ>"} all combined</li>
                                    </ul>

                                    In order to specify different dihedral angle conformations for evaluation, a set of keywords that identifies frequently used dihedral angle conformations
                                    (associated to individual and multiple dihedral angle terms) are used.
                                    These keywords can be used independently or combined to define various combinations
                                    of dihedral angle conformations to be evaluated.
                                    Below lists the dihedral angle group nomenclature used in PhiSiCal-Checkup.
                                </Col>
                            </Row>

                            <Row>
                                <Col span={22} style={{
                                    marginLeft: '3vh',
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "center"
                                }}>
                                    <Alert
                                        style={{marginBottom: "min(1vh, 10px)"}}
                                        message={"Dihedral angle group nomenclature"}
                                        type="info"
                                        showIcon
                                        description={
                                            <List
                                                size="small"
                                                dataSource={DIHEDRAL_ANGLE_GROUP_DESCRIPTIONS}
                                                renderItem={(item, index) => (
                                                    <List.Item
                                                        style={{paddingBottom: 4, paddingTop: (index === 0 ? 6 : 4)}}>
                                                        <Row style={{width: '100%'}}>
                                                            <Col md={5} lg={5} xl={4} xxl={6}>
                                                                <Typography.Text strong
                                                                                 style={{fontSize: 15}}>{item[0]}</Typography.Text>
                                                            </Col>
                                                            <Col md={19} lg={19} xl={20} xxl={18}
                                                                 style={{fontSize: '0.9rem'}}>
                                                                {item[1]}
                                                            </Col>
                                                        </Row>
                                                    </List.Item>
                                                )}
                                            />}/>
                                </Col>
                            </Row>

                            <Row id={"configuring_marginal_conformations"} style={{scrollMarginTop:'10vh', scrollSnapMarginTop:'10vh'}}>
                                <Col span={22}>
                                    <Typography.Title level={5}> Configuring marginal conformations for
                                        evaluation </Typography.Title>
                                    <span style={{whiteSpace: "break-spaces"}}>
                                        This interface can be used to specify conformations resultant from combining (one
                                    or more) dihedral angle terms.
                                    If no explicit specifications are provided, A default set of conformations are used for evaluation.
                                    </span>

                                    <div style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 20,
                                        flexWrap: "wrap",
                                        marginTop: "1vh",
                                        marginBottom: "1vh"
                                    }}>
                                        <img style={{alignSelf: "center"}}
                                             src={process.env.PUBLIC_URL + "/tutorial/marginal_configs.png"}
                                             alt="Marginal conformations."/>
                                    </div>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                    Use <Progress type="circle" success={{
                                    percent: 99.9, strokeColor: "#1eed19"
                                }} format={() => '7'} size={28}/> to add conformations or  <Progress type="circle"
                                                                                success={{percent: 99.9, strokeColor: "#1eed19"}}
                                                                                format={() => '8'} size={28}/> to remove conformations.{"\n"}

                                    On clicking <Button type="primary" ghost icon={<PlusCircleOutlined/>}
                                                        size={"small"}>Add New Conformation</Button> <Progress type="circle" success={{
                                    percent: 99.9, strokeColor: "#1eed19"
                                }} format={() => '7'} size={28}/>,
                                    a dropdown menu similar to <Select
                                    mode="multiple"
                                    style={{width: '10%'}}
                                    placeholder="Select conformations"
                                    filterOption={true}
                                    allowClear={true}
                                    onChange={(options) => {
                                        setMarginalConfiguration(mapFunc(options))
                                    }}
                                    options={marginalConfiguration}/> will appear and it can be used to select and build dihedral angle
                                    groups.
                                    </span>
                                </Col>
                            </Row>

                            <Row id={"configuring_conditional_conformations"} style={{scrollMarginTop:'10vh', scrollSnapMarginTop:'10vh'}}>
                                <Col span={22}>
                                    <Typography.Title level={5}>Configuring conditional conformations for
                                        evaluation </Typography.Title>
                                    <span style={{whiteSpace: "break-spaces"}}>
                                    This interface can be used to specify conditioned dihedral angle conformations.
                                    Here a conditioned conformation can be used to evaluate the conformation of a subset
                                    of dihedral angles terms given/observing another subset of dihedral angle terms.

                                    If no explicit specifications are provided, the interface will include a default set
                                    of conditioned conformations for evaluation.
                                    </span>

                                    <div style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 20,
                                        flexWrap: "wrap",
                                        marginTop: "1vh"
                                    }}>
                                        <img style={{alignSelf: "center"}}
                                             src={process.env.PUBLIC_URL + "/tutorial/conditional_configs.png"}
                                             alt="Conditional conformations."/>
                                    </div>
                                    <span style={{whiteSpace: "break-spaces"}}>
                                    Use the dropdown <Progress type="circle"
                                                           success={{percent: 99.9, strokeColor: "#1eed19"}}
                                                           format={() => '9'} size={28}/> to
                                    specify the conditioned dihedral angle conformation.{"\n"}

                                    Use the dropdown <Progress
                                    type="circle" success={{percent: 99.9, strokeColor: "#1eed19"}} format={() => '10'}
                                    size={28}/> to specify the set of dihedral angle terms to conditioned on.
                                    For an example conditioned conformation included in the image below can be use to
                                    evaluate the conformation of side chain dihedral angle terms
                                    given {"<φ,ψ>"} dihedral angles (See PhiSiCal-Checkup dihedral angle nomenclature
                                    for the keywords used).{"\n"}

                                    Use the button <Progress type="circle" success={{percent: 99.9, strokeColor: "#1eed19"}}
                                                  format={() => '11'} size={28}/> to
                                    add new conformations or <Progress type="circle"
                                                                       success={{percent: 99.9, strokeColor: "#1eed19"}}
                                                                       format={() => '12'} size={28}/> to remove conformations.
                                    </span>
                                </Col>
                            </Row>

                            <Row id={"configuring_joint_conformations"} style={{scrollMarginTop:'10vh', scrollSnapMarginTop:'10vh'}}>
                                <Col span={22}>
                                    <Typography.Title level={5}>Configuring joint conformation for
                                        evaluation </Typography.Title>
                                    <span style={{whiteSpace: "break-spaces"}}>
                                    Use this interface to include or exclude the joint conformation
                                    ({"<ω, φ, ψ, χ₁, ..., Xₙ>"} all combined) from evaluation.
                                    </span>
                                    <div style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "center",
                                        gap: 20,
                                        flexWrap: "wrap",
                                        marginTop: "1vh"
                                    }}>
                                        <img style={{alignSelf: "center"}}
                                             src={process.env.PUBLIC_URL + "/tutorial/joint_configs.png"}
                                             alt="Joint conformations."/>
                                    </div>
                                    <span style={{whiteSpace: "break-spaces"}}>
                                    Select/Deselect checkbox <Progress type="circle"
                                                                success={{percent: 99.9, strokeColor: "#1eed19"}}
                                                                format={() => '13'} size={28}/> to include/exclude the joint conformation from evaluation.
                                    </span>
                                </Col>
                            </Row>


                            <Row id="phisical_checkup_output" style={{scrollMarginTop:'10vh', scrollSnapMarginTop:'10vh'}}>
                                <Col span={22}>
                                    <Typography.Title level={3}> PhiSiCal-Checkup output </Typography.Title>
                                    Once the evaluation is completed for the specified PDB ID or uploaded atomic
                                    coordinate file, the output will be displayed
                                    in three different panels.
                                    <ul style={{marginTop: "1vh"}}>
                                        <li><b> PhiSiCal-Checkup report</b> - Display a quantitative evaluation of amino acid dihedral angle conformations
                                        </li>
                                        <li><b> Structure </b> - An interactive panel that displays dihedral
                                            angle conformation instances
                                            in <i>allowed</i> and <i>outlier </i> validation regions (highlighted) on
                                            the 3D structure
                                        </li>
                                        <li><b> Contour Analysis</b> - An interactive panel that displays
                                            dihedral angle conformations on pairwise
                                            contour plots
                                        </li>
                                    </ul>
                                </Col>
                            </Row>

                            <Row id="phisical_checkup_output_table" style={{scrollMarginTop:'10vh', scrollSnapMarginTop:'10vh'}}>
                                <Col span={22}>
                                    <Typography.Title level={4}> PhiSiCal-Checkup Report </Typography.Title>
                                    <div style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "center",
                                        gap: 20,
                                        flexWrap: "wrap",
                                        marginTop: "1vh"
                                    }}>
                                        <img style={{alignSelf: "center"}}
                                             src={process.env.PUBLIC_URL + "/tutorial/phisical_checkup_validation_report.png"}
                                             alt="Validation report"/>
                                    </div>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                    <Progress type="circle" success={{percent: 99.9, strokeColor: "#ea740c"}}
                                              format={() => '1'} size={40}/>  is a multi-criterion table that
                                    contains a quantitative evaluation of a set of dihedral angle conformations (starting from column 5 onwards)
                                    for each amino acid entry (rows in the table) in the protein.
                                    Each cell in the table contains log-odds of observing the respective conformation
                                    arising from PhiSiCal-Mixture models compared to the uniform null model. This is quantified as the <i>compression</i> gained (+ values) or lost (- values) in terms of bits.
                                    Specifically, for individual dihedral angles {"ω, φ, ψ, χ₁, ..., χ₅"} cells are divided in to two parts: top section displays the compression value
                                    and bottom section displays the observed dihedral angle in degrees.{"\n"}
                                    </span>
                                    <span style={{whiteSpace: "break-spaces"}}>
                                        Conformation evaluation for each amino acid in the protein is shown in a single row except for Proline residues.
                                        For each entry of Prolines, the table will contain two rows that contain results evaluated from two different mixture models.
                                        The first row contains compression values from the PRO amino acid specific mixture model
                                        and the second row contains results from the PRO conditioned model (conditioned on the {"ω"} angle).{"\n"}
                                    </span>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                    Icon <InfoCircleTwoTone twoToneColor={'#426ffa'}/> <Progress type="circle"
                                                                                                 success={{
                                                                                                     percent: 99.9,
                                                                                                     strokeColor: "#ea740c"
                                                                                                 }} format={() => '2'}
                                                                                                 size={28}/> indicates conformations
                                        with overlapping memberships. Hover over <InfoCircleTwoTone
                                        twoToneColor={'#426ffa'}/> to view the additional info of the perturbed membership (after a minor perturbation in the direction of the gradient) and
                                        the resultant dihedral angles of the perturbed conformation.{"\n"}
                                    </span>


                                    <span style={{whiteSpace: "break-spaces"}}>
                                    Use {<SearchOutlined/>} <Progress type="circle"
                                                                      success={{percent: 99.9, strokeColor: "#ea740c"}}
                                                                      format={() => '3'} size={28}/> to filter amino acid entries in the table.
                                        Clicking {<SearchOutlined/>} on any conformation (column) allows users to input a compression value and filter all the amino acid entries having compression <FontAwesomeIcon
                                        icon={faLessThan}/> the input value for the filtered conformation.
                                        For an example, an input value of -7 for the {"χ₅"} conformation, filters and shows only amino acid entries having compression <FontAwesomeIcon
                                        icon={faLessThan}/> -7 (bits) in
                                        their {"χ₅"} dihedral angle conformation evaluation.{"\n"}
                                    </span>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                        Toggle <Progress type="circle" success={{percent: 99.9, strokeColor: "#ea740c"}}
                                                         format={() => '4'} size={28}/> to visualize individual dihedral angles on the circle.{"\n"}
                                    </span>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                        <Progress type="circle" success={{percent: 99.9, strokeColor: "#ea740c"}}
                                                  format={() => '5'} size={28}/> and <Progress type="circle" success={{
                                        percent: 99.9,
                                        strokeColor: "#ea740c"
                                    }} format={() => '6'} size={28}/> are
                                        downloadable files. PhiSiCal-Checkup report in textual format and scripts to visualize <i> allowed </i> and <i>outlier</i> conformations in PyMOL.{"\n"}
                                    </span>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                        <Progress type="circle" success={{percent: 99.9, strokeColor: "#ea740c"}}
                                                  format={() => '7'} size={28}/> shows the keywords used in PhiSiCal-Checkup
                                        to identify frequently used dihedral angle conformations.{"\n"}
                                    </span>

                                    <span style={{whiteSpace: "break-spaces", display: "flex", marginTop: "2vh"}}>
                                        Each cell (conformation evaluation) in the table is assigned a color based on the associated validation region
                                        (among the three categories <Tag color={OUTLIER_COLOR_LIGHT} style={{
                                        fontSize: "0.9em",
                                        color: "black"
                                    }}>Outlier</Tag>, <Tag color={ALLOWED_COLOR_LIGHT} style={{
                                        fontSize: "0.9em",
                                        color: "black"
                                    }}>Allowed</Tag>,<Tag color={FAVORED_COLOR_LIGHT}
                                                              style={{fontSize: "0.9em", color: "black"}}>Favoured</Tag>) that
                                        they belong to. See section
                                        <a href={"#phisical_checkup_coloring"}> Assigning conformations to validation regions</a> for more details.{"\n"}
                                    </span>
                                </Col>
                            </Row>


                            <Row id="phisical_checkup_output_on_structure" style={{scrollMarginTop:'10vh', scrollSnapMarginTop:'10vh'}}>
                                <Col span={22}>
                                    <Typography.Title level={4}> Visualize on structure </Typography.Title>
                                    <div style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "center",
                                        gap: 20,
                                        flexWrap: "wrap",
                                        marginTop: "1vh"
                                    }}>
                                        <img style={{alignSelf: "center", width: "99%"}}
                                             src={process.env.PUBLIC_URL + "/tutorial/structure_panel.png"}
                                             alt="Structure panel"/>
                                    </div>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                    The <b>Structure</b> panel <Progress type="circle" success={{
                                        percent: 99.9,
                                        strokeColor: "#0909e0"
                                    }} format={() => '1'} size={40}/> aids
                                    visualizing the <Tag color={"#F6e5BB"} style={{
                                        fontSize: "0.9em",
                                        color: "black"
                                    }}> Allowed </Tag> and <Tag color={OUTLIER_COLOR_DARK} style={{
                                        fontSize: "0.9em",
                                        color: "black"
                                    }}> Outlier </Tag> conformations on the 3D protein structure.{"\n"}
                                    </span>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                        Use the dropdown menu <Progress type="circle" success={{
                                        percent: 99.9,
                                        strokeColor: "#0909e0"
                                    }} format={() => '2'} size={28}/> to
                                        select the type of dihedral angle conformation to view on the structure.{"\n"}
                                    </span>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                        Use the radio buttons <Progress type="circle" success={{
                                        percent: 99.9,
                                        strokeColor: "#0909e0"
                                    }} format={() => '3'} size={28}/> to
                                        change the background color of the visualizing panel.{"\n"}
                                    </span>

                                    This interface is embedded with <a href={"https://nglviewer.org/"} target={"_blank"}
                                                                       rel={"noopener noreferrer"}>NGL Viewer</a> to
                                    render the 3D protein structure model and to visualize online.
                                </Col>
                            </Row>

                            <Row id="phisical_checkup_output_contour_analysis" style={{scrollMarginTop:'10vh', scrollSnapMarginTop:'10vh'}}>
                                <Col span={22}>
                                    <Typography.Title level={4}> Contour analysis </Typography.Title>
                                    <span style={{whiteSpace: "break-spaces"}}>
                                    Contour plot analysis facilitates visualizing dihedral angle conformations on pair-wise contour plots.{"\n"}
                                    </span>
                                    <div style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "center",
                                        gap: 20,
                                        flexWrap: "wrap",
                                        marginTop: "1vh"
                                    }}>
                                        <img style={{alignSelf: "center"}}
                                             src={process.env.PUBLIC_URL + "/tutorial/contour_analysis_panel.png"}
                                             alt="Contour analysis panel"/>
                                    </div>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                        Use <Progress type="circle" success={{percent: 99.9, strokeColor: "#ea0cd8"}}
                                                      format={() => '1'} size={28}/> to select the pair-wise dihedral angle conformation for the contour analysis.
                                        The dropdown menu is populated with the pairwise dihedral angle conformations specified when configuring <a href={"#configuring_marginal_conformations"}> Marginal conformations</a> for evaluation.
                                        If default marginal configurations are used, contour analysis will be performed only on the {"<φ,ψ>"} dihedral angle conformation (Ramachandran conformation). {"\n"}
                                    </span>
                                    <span style={{whiteSpace: "break-spaces"}}>
                                        Use <Progress type="circle" success={{percent: 99.9, strokeColor: "#ea0cd8"}}
                                                      format={() => '2'} size={28}/> to select the validation region.
                                        <b> None</b> selects all conformations irrespective of their assigned validation region.{"\n"}
                                    </span>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                        Use <Progress type="circle" success={{percent: 99.9, strokeColor: "#ea0cd8"}}
                                                      format={() => '3'} size={28}/> to show all the amino acid types.
                                        PhiSiCal-Checkup uses mixture models inferred from amino acid specific dihedral angle distributions to perform conformation validation.
                                        Hence, selecting <b> ALL </b> will only show a 2D scatter plot with all the amino acid entries with {"φ, ψ"} dihedral angles defined.
                                        To view on contour plots, select a amino acid type in <Progress
                                        type="circle" success={{percent: 99.9, strokeColor: "#ea0cd8"}}
                                        format={() => '5'} size={28}/>.{"\n"}
                                    </span>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                        Use <Progress type="circle" success={{percent: 99.9, strokeColor: "#ea0cd8"}}
                                                      format={() => '4'} size={28}/> to show pre-proline residues in their amino acid
                                        specific contour plots.{"\n"}
                                    </span>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                     Depending on the options selected in <Progress type="circle" success={{
                                        percent: 99.9,
                                        strokeColor: "#ea0cd8"
                                    }} format={() => '1'} size={28}/> (conformation), <Progress type="circle" success={{
                                        percent: 99.9,
                                        strokeColor: "#ea0cd8"
                                    }} format={() => '2'} size={28}/> (validation region), <Progress type="circle" success={{
                                        percent: 99.9,
                                        strokeColor: "#ea0cd8"
                                    }} format={() => '3'} size={28}/> (all amino acids) or <Progress type="circle" success={{
                                        percent: 99.9,
                                        strokeColor: "#ea0cd8"
                                    }} format={() => '5'} size={28}/> (amino acid type) and <Progress type="circle" success={{
                                        percent: 99.9,
                                        strokeColor: "#ea0cd8"
                                    }} format={() => '4'} size={28}/> (pre-proline or not) the data distribution on the contour/scatter plot changes dynamically.{"\n"}
                                    </span>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                     As examples, Selecting
                                        <ol type={"A"}>
                                            <li><b>None</b>, <b>ALL</b> - shows all amino acids on the scatter plot, irrespective of their validation region</li>
                                            <li><b>Outlier</b>, <b>ALL</b> - shows all amino acids in the outlier validation region</li>
                                             <li><b>Outlier</b>, <b>THR</b> - shows all Threonine entries in the outlier validation region</li>
                                             <li><b>Favoured</b>, <b>LEU</b>, <Checkbox
                                                 checked={true}>pre-proline</Checkbox>- shows all pre-proline Leucine entries in the Favoured validation region </li>
                                        </ol>
                                        <div style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "center",
                                            gap: 20,
                                            flexWrap: "wrap",
                                            marginTop: "1vh"
                                        }}>
                                            <div style={{display: "flex", flexDirection: "column"}}>
                                                <div style={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    fontSize: '1.5em'
                                                }}>A.</div>
                                                <div>
                                                    <img style={{alignSelf: "center"}}
                                                         src={process.env.PUBLIC_URL + "/tutorial/all_scatter_plot.png"}
                                                         alt="ALL residue scatter plot"/>
                                                </div>
                                            </div>

                                            <div style={{display: "flex", flexDirection: "column"}}>
                                                <div style={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    fontSize: '1.5em'
                                                }}>B.</div>
                                                <div>
                                                    <img style={{alignSelf: "center"}}
                                                         src={process.env.PUBLIC_URL + "/tutorial/all_outlier.png"}
                                                         alt="ALL outlier scatter plot"/>
                                                </div>
                                            </div>

                                            <div style={{display: "flex", flexDirection: "column", fontSize: '1.5em'}}>
                                                <div style={{display: "flex", justifyContent: "center"}}>C.</div>
                                                <div>
                                                    <img style={{alignSelf: "center"}}
                                                         src={process.env.PUBLIC_URL + "/tutorial/thr_outlier.png"}
                                                         alt="THR outlier scatter plot"/>
                                                </div>
                                            </div>

                                             <div style={{display: "flex", flexDirection: "column", fontSize: '1.5em'}}>
                                               <div style={{display: "flex", justifyContent: "center"}}>D.</div>
                                                 <div>
                                                     <img style={{alignSelf: "center"}}
                                                          src={process.env.PUBLIC_URL + "/tutorial/pre_proline_LEU.png"}
                                                          alt="LEU Pre-Proline in Favoured"/>
                                                 </div>
                                            </div>
                                    </div>
                                    </span>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                       Use <Progress type="circle" success={{percent: 99.9, strokeColor: "#ea0cd8"}}
                                                     format={() => '6'} size={28}/> to show a filtered set of amino acids on the contour/scatter plot.{"\n"}
                                    </span>

                                    <span style={{whiteSpace: "break-spaces"}}>
                                       Contour/scatter plots are interactive.
                                        <ul>
                                            <li>Hover over data points to view residue information <a
                                                href={process.env.PUBLIC_URL + "/tutorial/contour_plot_hover.gif"}
                                                target={"_blank"}
                                                rel={"noopener noreferrer"}
                                            ><SelectOutlined rotate={90}/> </a></li>
                                            <li>Click on data points to view conformation evaluation <a
                                                href={process.env.PUBLIC_URL + "/tutorial/contour_analysis_show_info.gif"}
                                                target={"_blank"} rel={"noopener noreferrer"}><SelectOutlined
                                                rotate={90}/> </a></li>
                                             <li>Click anywhere on the plot. Press ALT key and use mouse wheel to zoom in an out <a
                                                 href={process.env.PUBLIC_URL + "/tutorial/contour_plot_zoom.gif"}
                                                 target={"_blank"} rel={"noopener noreferrer"}><SelectOutlined
                                                 rotate={90}/> </a></li>
                                        </ul>
                                    </span>

                                </Col>
                            </Row>

                            <Row id="phisical_checkup_coloring" style={{scrollMarginTop:'10vh', scrollSnapMarginTop:'10vh'}}>
                                <Col span={22}>
                                    <Typography.Title level={3}> Assigning conformations to validation
                                        regions </Typography.Title>
                                    <div style={{display: "flex", justifyContent: "center", flexDirection: "row"}}>
                                        <img style={{alignSelf: "center"}}
                                             src={process.env.PUBLIC_URL + "/tutorial/assigning_membership.png"}
                                             alt="Customize validation threshold values"/>
                                    </div>
                                    <span style={{whiteSpace: "break-spaces"}}>
                                        By default, PhiSiCal-Checkup sets <i>Outlier</i> threshold at -4
                                        bits, <i>Allowed</i> threshold at 0 bits.
                                        With this hard cut-off values, any conformation that has its
                                        <ul>
                                           <li>compression value <FontAwesomeIcon icon={faLessThan}/> -4 bits gets assigned to the <Tag color={OUTLIER_COLOR_LIGHT} style={{
                                               fontSize: "0.9em",
                                               color: "black"
                                           }}>Outlier</Tag> region</li>
                                           <li>-4 bits <FontAwesomeIcon
                                               icon={faLessThanEqual}/> compression value <FontAwesomeIcon
                                               icon={faLessThan}/> 0 bits gets assigned to the <Tag color={ALLOWED_COLOR_LIGHT}
                                                                                                    style={{fontSize: "0.9em", color: "black"}}>Allowed</Tag> region</li>
                                           <li> compression value <FontAwesomeIcon icon={faGreaterThanEqual}/> 0 bits gets assigned to the <Tag color={FAVORED_COLOR_LIGHT} style={{
                                               fontSize: "0.9em",
                                               color: "black"
                                           }}>Favoured</Tag> region</li>
                                        </ul>
                                        Further, the default threshold values can be customized using input fields <Progress
                                        type="circle" success={{percent: 99.9, strokeColor: "#115060"}}
                                        format={() => '1'} size={28}/> and <Progress type="circle" success={{
                                        percent: 99.9,
                                        strokeColor: "#115060"
                                    }} format={() => '2'} size={28}/>.{"\n"}
                                    </span>
                                    <span style={{whiteSpace: "break-spaces"}}>
                                        Use the input field <Progress type="circle"
                                                                      success={{percent: 99.9, strokeColor: "#115060"}}
                                                                      format={() => '1'} size={28}/> to update <Tag
                                        color={OUTLIER_COLOR_LIGHT}
                                        style={{fontSize: "0.9em", color: "black"}}>Outlier</Tag>threshold.{"\n"}
                                    </span>
                                    <span style={{whiteSpace: "break-spaces"}}>
                                        Use the input field <Progress type="circle"
                                                                      success={{percent: 99.9, strokeColor: "#115060"}}
                                                                      format={() => '2'} size={28}/> to update <Tag
                                        color={ALLOWED_COLOR_LIGHT}
                                        style={{fontSize: "0.9em", color: "black"}}>Allowed</Tag>threshold.
                                        <b> Note</b>: Outlier threshold should be <FontAwesomeIcon icon={faLessThan}/> Allowed threshold.{"\n"}
                                    </span>
                                    <span style={{whiteSpace: "break-spaces"}}>
                                        Use <Button type="primary">Apply</Button> <Progress type="circle" success={{
                                        percent: 99.9,
                                        strokeColor: "#115060"
                                    }} format={() => '3'} size={28}/> to apply new threshold values to across all output panels. This automatically sets
                                        the new threshold of the <Tag color={FAVORED_COLOR_LIGHT} style={{
                                        fontSize: "0.9em",
                                        color: "black"
                                    }}>Favoured</Tag> region as well.{"\n"}
                                    </span>
                                </Col>
                            </Row>

                            <Row style={{justifyContent: "left"}}>
                                <Col style={{marginBottom: "2vh"}} span={22}>
                                    <Typography.Title level={3}>Contact us</Typography.Title>
                                    In case you experience any trouble using PhiSiCal-Checkup or if you have any
                                    comments or feature requests
                                    please do not hesitate in contacting us.
                                    You can use the <a
                                    href={"https://lcb.infotech.monash.edu/phisical/checkup/bugreports"}
                                    target={"_blank"} rel={"noopener noreferrer"}>BugReports portal </a>
                                    to contact us.
                                </Col>
                            </Row>
                        </ConfigProvider>
                    </Layout.Content>
                    <Layout.Footer
                        style={{width: '100%', textAlign: 'right', zIndex: 1000, backgroundColor: "#58B2DC"}}><b>About
                        <a href="https://lcb.infotech.monash.edu/phisical/" target="_blank" rel="noreferrer"
                           style={{color: "#1402f6"}}> 𝜙𝛹𝜒al</a> |
                        <a href="https://lcb.infotech.monash.edu/" target="_blank" rel="noreferrer"
                           style={{color: "#1402f6"}}> LCB Home</a> |v1.0.0 </b>
                    </Layout.Footer>
                </Layout>
            </div>
        </div>
    );
}

export default Tutorial;